// Indian GST from the books: what we charged (Output GST, account
// output-vat), what we can claim (Input GST, input-vat) and what was paid
// to the government (Bank › Pay GST entries, sourceType 'tax_adjustment').

import { A, bankAccountSlug } from './ledger';
import type { JournalEntry } from '../types';

const round2 = (n: number) => Math.round(n * 100) / 100;
const isBankLine = (accountId: string) => accountId.startsWith(bankAccountSlug(''));

/** Balances now: Output GST we owe, Input GST we hold, and owed − held. */
export function gstBalances(entries: JournalEntry[]) {
  let output = 0, input = 0;
  for (const e of entries) for (const l of e.lines ?? []) {
    if (l.accountId === A.outputVat) output += (l.credit ?? 0) - (l.debit ?? 0);
    if (l.accountId === A.inputVat)  input  += (l.debit ?? 0) - (l.credit ?? 0);
  }
  return { output: round2(output), input: round2(input), net: round2(output - input) };
}

export interface GstMonth {
  month: string;      // YYYY-MM
  output: number;     // GST charged on sales (and owed on reverse charge), less returns
  input: number;      // GST paid on purchases / expenses / brokerage that we can claim
  paid: number;       // paid to the government from the bank
  balance: number;    // at month end: + owed to the government, − credit carried forward
}

/** One row per month with GST activity, oldest first. */
export function gstMonths(entries: JournalEntry[]): GstMonth[] {
  const map = new Map<string, GstMonth>();
  const get = (m: string) => {
    let r = map.get(m);
    if (!r) { r = { month: m, output: 0, input: 0, paid: 0, balance: 0 }; map.set(m, r); }
    return r;
  };
  for (const e of entries) {
    const m = (e.date ?? '').slice(0, 7);
    if (!m) continue;
    const settle = e.sourceType === 'tax_adjustment';
    let touched = false;
    for (const l of e.lines ?? []) {
      if (l.accountId === A.outputVat && !settle) { get(m).output += (l.credit ?? 0) - (l.debit ?? 0); touched = true; }
      if (l.accountId === A.inputVat && !settle)  { get(m).input  += (l.debit ?? 0) - (l.credit ?? 0); touched = true; }
      if (settle && isBankLine(l.accountId))     { get(m).paid   += (l.credit ?? 0) - (l.debit ?? 0); touched = true; }
    }
    if (settle && !touched) get(m);
  }
  const rows = [...map.values()].sort((a, b) => a.month.localeCompare(b.month));
  let bal = 0;
  for (const r of rows) {
    r.output = round2(r.output); r.input = round2(r.input); r.paid = round2(r.paid);
    bal = round2(bal + r.output - r.input - r.paid);
    r.balance = bal;
  }
  return rows;
}
